import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isItemActiveRecursive } from "../../utils/sidebarUtils";

const SidebarItem = ({ item, isMinimized, depth = 0, isOpen, onToggle }) => {
  const { t } = useTranslation();
  const [openChildId, setOpenChildId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const isActive = useMemo(
    () => isItemActiveRecursive(item, currentPath),
    [item, currentPath]
  );

  // Auto-expand if active and has children on path change
  useEffect(() => {
    if (!isMinimized && isActive && item.childrens && !isOpen) {
      onToggle();
    }
  }, [currentPath]);

  useEffect(() => {
    if (!isMinimized && item.childrens) {
      const activeChild = item.childrens.find((child) =>
        isItemActiveRecursive(child, currentPath)
      );
      if (activeChild) {
        setOpenChildId(activeChild.title);
      }
    }
  }, [currentPath]);

  const handleToggle = () => !isMinimized && onToggle();
  const handleNavigation = (e) => {
    e.preventDefault();
    navigate(item.path || "/loading");
  };
  const handleShortcutClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(item.shortcutPath);
  };

  // Helper classes
  const hasGrandchildren = item.childrens?.some(
    (child) => child.childrens?.length > 0,
  );
  const isParentOfInnermostChildren =
    depth === 1 && item.childrens && !hasGrandchildren;
  const isInnermostChild =
    depth === 2 && (!item.childrens || item.childrens.length === 0);

  const itemClasses = `sidebar-item ${isOpen ? "open" : ""} ${isActive && depth === 0 ? "active" : ""} ${isParentOfInnermostChildren ? "parent-of-innermost" : ""}`;

  const titleClasses = `sidebar-title ftw-4 ${isParentOfInnermostChildren ? "blue" : "dk-gray"} common-text ${isActive && depth === 0 ? "active" : ""}`;

  const plainClasses = `sidebar-item plain ftw-4 dk-gray ${isActive ? "active" : ""} ${isInnermostChild ? "innermost-child" : ""}`;
  const isTopLevelWithIcon = depth === 0 && item.icon;

  // if (item.childrens || item.path === "/dashboard") {
  if (item.childrens) {
    return (
      <div className={itemClasses}>
        <div className={titleClasses} onClick={handleToggle}>
          {item.icon ? (
            <img
              src={`/assets/${item.icon}.png`}
              alt={item.title}
              className="sidebar-icon"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={item.title}
            />
          ) : item.childrens ? (
            <img
              src={
                isParentOfInnermostChildren
                  ? "/assets/black-dot.png"
                  : "/assets/dot-icon.png"
              }
              alt={item.title}
              className="sidebar-icon-dot"
            />
          ) : null}
          {!isMinimized && <span>{t(item.title)}</span>}
          {item.childrens && !isMinimized && (
            <img
              src="/assets/Expand.png"
              alt="toggle"
              className={`toggle-btn ${isOpen ? "rotated" : ""}`}
            />
          )}
        </div>
        {item.childrens && isOpen && (
          <div className="sidebar-content">
            {item.childrens.map((child, index) => (
              <SidebarItem
                key={child.title || index}
                item={child}
                isMinimized={isMinimized}
                depth={depth + 1}
                isOpen={openChildId === child.title}
                onToggle={() =>
                  setOpenChildId(openChildId === child.title ? null : child.title)
                }
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <a
      href={item.path || "/loading"}
      className={plainClasses}
      style={{
        ...(isInnermostChild ? { padding: "0.5rem 2rem" } : {}),
        ...(depth === 0 && (!item.childrens || item.childrens.length === 0)
          ? { gap: "0px" }
          : {}),
      }}
      onClick={handleNavigation}
    >
      {isTopLevelWithIcon ? (
        <img
          src={`/assets/${item.icon}.png`}
          alt={item.title}
          className="sidebar-icon"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={t(item.title)}
        />
      ) : !isInnermostChild ? (
        <img
          src="/assets/black-dot.png"
          alt={item.title}
          className="sidebar-icon-dot"
        />
      ) : null}
      {!isMinimized && (
        <span
          style={{
            marginInlineEnd: isActive && item.shortcutPath ? "25px" : "0px",
          }}
        >
          {t(item.title)}
        </span>
      )}
      {!isMinimized && isActive && item.shortcutPath && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
          className="sidebar-plus-img"
          onClick={handleShortcutClick}
          data-tooltip-id="global-tooltip"
          data-tooltip-content={t("Create New")}
        >
          <rect width="21" height="21" rx="10.5" fill="#094C80" />
          <path
            d="M6 10.5H15"
            stroke="white"
            strokeWidth="1.46625"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.5002 6V15"
            stroke="white"
            strokeWidth="1.46625"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </a>
  );
};

SidebarItem.displayName = "SidebarItem";
export default SidebarItem;
